<script setup>
import { ref, computed, onMounted } from "vue";
import { useGlobalStore } from "@stores/global";
import { useTriviaStore } from "@stores/trivia";
import IconX from "@components/icons/X.vue";

const globalStore = useGlobalStore();
const triviaStore = useTriviaStore();

function close() {
    globalStore.hidePanel("trivia");
}

const leaderboardData = ref({ daily: [], monthly: [] });
const loading = ref(true);

const leaderboard = computed(() =>
    (leaderboardData.value[triviaStore.leaderboardTab] ?? []).map(
        (row, i) => ({
            rank: i + 1,
            username: row.username,
            points: row.score,
        }),
    ),
);

onMounted(async () => {
    try {
        const { data } = await axios.get("/api/trivia/leaderboard");
        leaderboardData.value = data;
    } finally {
        loading.value = false;
    }
});
</script>

<template>
    <Transition name="panel" appear>
        <div
            v-if="globalStore.panelActive('trivia')"
            class="trivia-sidebar bg-marine-800 border-marine-500 relative flex flex-col border-b"
        >
        <div class="flex flex-col gap-6 p-6 max-lg:p-4">
            <div class="flex items-center justify-between">
                <p class="h6 font-bold uppercase max-lg:hidden">Trivia</p>
                <p class="h6 font-bold uppercase lg:hidden">Leaderboard</p>
                <button
                    class="border-marine-600 shadow-marine-600 flex size-8 shrink-0 items-center justify-center rounded-[4px] border-2 bg-yellow-400 shadow-[0_4px_0] lg:hidden"
                    @click="close"
                >
                    <IconX class="text-marine-600 size-3" />
                </button>
            </div>
            <p class="body-sm max-lg:hidden">
                Guess the country from Speed's travel clue.<br />
                The closer your guess, the hotter the feedback.
            </p>
        </div>

        <div class="flex flex-col gap-6 px-6 pb-6 max-lg:px-4">
            <p class="body-xs font-bold uppercase opacity-60 max-lg:hidden">
                Leaderboard
            </p>

            <div class="border-marine-500 flex border-b">
                <button
                    :class="[
                        'body-sm flex-1 border-b-2 py-2 font-bold uppercase transition-colors',
                        triviaStore.leaderboardTab === 'daily'
                            ? 'border-yellow-400 text-yellow-400'
                            : 'border-transparent opacity-60',
                    ]"
                    @click="triviaStore.setLeaderboardTab('daily')"
                >
                    Daily
                </button>
                <button
                    :class="[
                        'body-sm flex-1 border-b-2 py-2 font-bold uppercase transition-colors',
                        triviaStore.leaderboardTab === 'monthly'
                            ? 'border-yellow-400 text-yellow-400'
                            : 'border-transparent opacity-60',
                    ]"
                    @click="triviaStore.setLeaderboardTab('monthly')"
                >
                    Monthly
                </button>
            </div>

            <div
                v-if="loading"
                class="flex items-center justify-center py-10"
            >
                <svg
                    class="size-6 animate-spin text-yellow-400"
                    viewBox="0 0 24 24"
                    fill="none"
                >
                    <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="3"
                        stroke-opacity="0.25"
                    />
                    <path
                        d="M22 12a10 10 0 0 0-10-10"
                        stroke="currentColor"
                        stroke-width="3"
                        stroke-linecap="round"
                    />
                </svg>
            </div>

            <p
                v-else-if="leaderboard.length === 0"
                class="body-sm py-10 text-center opacity-60"
            >
                No {{ triviaStore.leaderboardTab }} leaderboard entries yet.
            </p>

            <ul v-else class="flex flex-col gap-1">
                <li
                    v-for="row in leaderboard"
                    :key="row.rank"
                    :class="[
                        'body-sm border-marine-500 relative isolate flex items-center justify-between border p-3',
                        row.isUser &&
                            'border-yellow-400 before:absolute before:right-0 before:bottom-0 before:size-1.5 before:bg-yellow-400 after:absolute after:top-0 after:left-0 after:size-1.5 after:bg-yellow-400',
                    ]"
                >
                    <div class="flex items-center gap-0.5">
                        <span
                            :class="[
                                'rank w-6 font-bold',
                                row.isUser && 'text-yellow-400',
                            ]"
                        >
                            #{{ row.rank }}
                        </span>
                        <span
                            :class="[
                                row.isUser
                                    ? 'font-bold text-yellow-400'
                                    : 'font-medium faded-text',
                            ]"
                        >
                            {{ row.username }}
                        </span>
                    </div>
                    <span class="font-bold">
                        {{ row.points }}
                    </span>
                </li>
            </ul>
        </div>
    </div>
    </Transition>
</template>

<style scoped>
.trivia-sidebar {
    grid-column: -6 / -1;
    grid-row: 2 / -1;
    z-index: 2;
    overflow-y: auto;
}
@media (max-width: 1024px) {
    .trivia-sidebar {
        grid-column: 1 / -1;
        grid-row: -2 / -1;
        align-self: end;
        height: 50vh;
        max-height: 50vh;
        z-index: 50;
    }
}
.rank {
    text-shadow: 0 2px 0 var(--marine-base, #19233b);
    -webkit-text-stroke-width: 4px;
    -webkit-text-stroke-color: var(--Marine-600, #19233b);
    paint-order: stroke fill;
}
</style>
